import type React from "react";
import {useMemo} from "react";
import {Outlet} from "react-router";
import {observer} from "mobx-react-lite";
import {Stack} from "@mui/material";
// Not in this version: the tab bar.
// import {BtnGroupTabs, type BtnGroupTabItem} from "~/components/BtnGroupTabs/BtnGroupTabs";
// import {COLORS} from "~/style/colors-ts";
import {TaxonomyBuilderHeader} from "./components/TaxonomyBuilderHeader";
import {TaxonomyBuilderKpis} from "./components/TaxonomyBuilderKpis";
import {TaxonomySettingsButton} from "./components/TaxonomySettingsButton";
import {TaxonomyBuilderHowTo} from "./components/TaxonomyBuilderHowTo";
import {TaxonomyDraftActionBar} from "./components/TaxonomyDraftActionBar";
import {useActiveTaxonomyId} from "~/pages/taxonomy-builder-v2/hooks/useActiveTaxonomyId";
import {useTaxonomyQuery} from "~/pages/taxonomy-builder-v2/hooks/useTaxonomyQuery";
import {listTaxonomyChanges} from "~/pages/taxonomy-builder-v2/services/taxonomyChanges";
import type {TaxonomyBuilderOutletContext} from "~/pages/taxonomy-builder-v2/types/TaxonomyBuilderTypes";

/**
 * The taxonomy is loaded here, once for every tab: the tabs get it through the outlet context and
 * the bottom action bar's "Send for approval" shows the draft's changes.
 */
export const TaxonomyBuilderPage: React.FC = observer(() => {
    const taxonomyId = useActiveTaxonomyId();
    const {data: taxonomy, isLoading, isError} = useTaxonomyQuery(taxonomyId);
    const changes = useMemo(() => taxonomy ? listTaxonomyChanges(taxonomy.diff_tree) : [], [taxonomy]);
    const context: TaxonomyBuilderOutletContext = {taxonomyId, taxonomy, isLoading, isError};

    // Not in this version: the tab bar.
    // const tabs: BtnGroupTabItem[] = [
    //     {title: 'All categories', route: 'all'},
    //     {title: 'Suggestions', route: 'suggestions'},
    //     {title: 'Health findings', route: 'findings'},
    // ];

    return (
        <>
            {/* `pt` is the 16px gap under the top bar, `pb` the 16px gap between the tab's content and the action bar. */}
            <Stack gap={2} sx={{p: 2}}>
                <TaxonomyBuilderHeader/>
                {/* The settings button sits at the end of the KPI chips' row. */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                    <TaxonomyBuilderKpis taxonomy={taxonomy} isLoading={isLoading}/>
                    <TaxonomySettingsButton taxonomy={taxonomy}/>
                </Stack>
                <TaxonomyBuilderHowTo isLoading={isLoading}/>
                {/* Sticky tabs + outlet share a wrapper so Stack `gap` isn't between them;
                    the tabs' own `pb` is the only spacer and stays put while the content scrolls under. */}
                <div>
                    {/* Not in this version: All categories is the only tab. */}
                    {/* <BtnGroupTabs
                        items={tabs}
                        sx={{position: 'sticky', top: 0, zIndex: 1000, backgroundColor: COLORS.white[900], pb: 2}}
                    /> */}
                    <Outlet context={context}/>
                </div>
            </Stack>
            {/* Only once the taxonomy is there: no draft status or "Send for approval" while it loads. */}
            {taxonomy && <TaxonomyDraftActionBar changes={changes} taxonomy={taxonomy}/>}
        </>
    );
});
